import React, { useEffect, useState } from 'react'
import {
  Button,
  CircularProgress,
  FormHelperText,
  Grid,
  TextField,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useEditInfoMutation } from '../../../../store/api/generalInfoApi'
import { Store } from '../../../../utils/toast'
import { serverUrl } from '../../../../request'
import BannerUpload from './BannerUpload/BannerUpload'
import './GeneralPage.scss'
import { getStorageKey } from '../../../../utils/appType'

interface FileWithPreview extends File {
  preview: string
}

const c = 'general-page'

const GeneralPage = () => {
  const generalInfo =
    localStorage.getItem(getStorageKey('info')) &&
    JSON.parse(localStorage.getItem(getStorageKey('info')) || '{}')

  const { t } = useTranslation()
  const [editInfo] = useEditInfoMutation()
  const [files, setFiles] = useState<FileWithPreview[]>([])
  const [loading, setLoading] = useState(false)

  const [images, setImages] = useState(
    generalInfo?.images.map((image: string) => serverUrl + image),
  )

  const {
    register,
    getValues,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm()

  function submit() {
    setLoading(true)
    const formData = new FormData()

    Object.entries(getValues()).forEach((item: [string, unknown]) => {
      formData.append(item[0], item[1] as string)
    })

    if (files.length) {
      files.forEach((file: FileWithPreview) => {
        formData.append('images', file)
      })
    }

    editInfo(formData)
      .unwrap()
      .then((res) => {
        const params = {
          ...getValues(),
          images: res.images.map((image: string) => serverUrl + image),
        }

        localStorage.setItem(getStorageKey('info'), JSON.stringify(params))
        setImages(params.images)

        Store.addNotification({
          title: 'SUCCESS',
          message: 'Success',
          container: 'top-center',
          type: 'success',
          insert: 'top',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: {
            duration: 1000,
            onScreen: true,
          },
        })
      })
      .catch(() => {
        Store.addNotification({
          title: 'ERROR',
          message: 'Error',
          container: 'top-center',
          type: 'danger',
          insert: 'top',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: {
            duration: 1000,
            onScreen: true,
          },
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (generalInfo) {
      setValue('phone1', generalInfo.phone1)
      setValue('phone2', generalInfo.phone2)
      setValue('minTime', generalInfo.minTime)
      setValue('maxTime', generalInfo.maxTime)
      setValue('minPrice', generalInfo.minPrice)
      setValue('dayOff', generalInfo.dayOff)
      setValue('announcement', generalInfo.announcement)
      setValue('homePromoMessage', generalInfo.homePromoMessage)
      setValue('cartPromoMessage', generalInfo.cartPromoMessage)
      setValue('rouletteRules', generalInfo.rouletteRules)
    }
  }, [])

  return (
    <form onSubmit={handleSubmit(submit)} className={c}>
      <div className={`${c}__title`}>General</div>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('phone1', { required: true })}
            placeholder='Telefon 1'
            error={!!errors.phone1}
            fullWidth
            variant='outlined'
            size='small'
            inputProps={{
              style: {
                borderColor: 'red',
              },
            }}
          />
          {errors?.phone1 && (
            <FormHelperText error={!!errors?.phone1} id='component-error-text'>
              {t('FIELD_REQUIRED')}
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('phone2', { required: true })}
            placeholder='Telefon 2'
            error={!!errors.phone2}
            fullWidth
            variant='outlined'
            size='small'
            inputProps={{
              style: {
                borderColor: 'red',
              },
            }}
          />
          {errors?.phone2 && (
            <FormHelperText error={!!errors?.phone2} id='component-error-text'>
              {t('FIELD_REQUIRED')}
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('minTime', { required: true })}
            placeholder='Livrarea de la:'
            error={!!errors.minTime}
            fullWidth
            variant='outlined'
            size='small'
            inputProps={{
              style: {
                borderColor: 'red',
              },
            }}
          />
          {errors?.minTime && (
            <FormHelperText error={!!errors?.minTime} id='component-error-text'>
              {t('FIELD_REQUIRED')}
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('maxTime', { required: true })}
            placeholder='Livrarea pana la:'
            error={!!errors.maxTime}
            fullWidth
            variant='outlined'
            size='small'
            inputProps={{
              style: {
                borderColor: 'red',
              },
            }}
          />
          {errors?.maxTime && (
            <FormHelperText error={!!errors?.maxTime} id='component-error-text'>
              {t('FIELD_REQUIRED')}
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...register('minPrice', { required: true })}
            placeholder='Pretul minim'
            error={!!errors.minPrice}
            fullWidth
            variant='outlined'
            size='small'
            inputProps={{
              style: {
                borderColor: 'red',
              },
            }}
          />
          {errors?.minPrice && (
            <FormHelperText
              error={!!errors?.minPrice}
              id='component-error-text'
            >
              {t('FIELD_REQUIRED')}
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12}>
          <label>Zile Libere</label>
          <TextField
            {...register('dayOff')}
            placeholder='Zile Libere'
            multiline
            rows={3}
            fullWidth
            variant='outlined'
            size='small'
            inputProps={{
              style: {
                borderColor: 'red',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <label>Anunț</label>
          <TextField
            {...register('announcement')}
            placeholder='Anunț'
            multiline
            rows={3}
            fullWidth
            variant='outlined'
            size='small'
            inputProps={{
              style: {
                borderColor: 'red',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <label>Mesaj de promoție</label>
          <TextField
            {...register('homePromoMessage')}
            placeholder='Mesaj de promoție'
            multiline
            rows={3}
            fullWidth
            variant='outlined'
            size='small'
            inputProps={{
              style: {
                borderColor: 'red',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <label>Mesaj de promoție în coş</label>
          <TextField
            {...register('cartPromoMessage')}
            placeholder='Mesaj de promoție în coş'
            multiline
            rows={3}
            fullWidth
            variant='outlined'
            size='small'
            inputProps={{
              style: {
                borderColor: 'red',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <label>Regulile ruletei</label>
          <TextField
            {...register('rouletteRules')}
            placeholder='Regulile ruletei'
            multiline
            rows={3}
            fullWidth
            variant='outlined'
            size='small'
            inputProps={{
              style: {
                borderColor: 'red',
              },
            }}
          />
        </Grid>
      </Grid>
      <div className={`${c}__banner`}>
        <BannerUpload
          files={files}
          setFiles={setFiles}
          setImages={setImages}
          images={images}
        />
      </div>

      <div className={`${c}__submit`}>
        <Button
          variant='contained'
          type='submit'
          disabled={loading}
          sx={{
            backgroundColor: 'var(--color-primary)',
            textTransform: 'none',
            fontWeight: 'bold',
            width: '100%',
            '&:hover': { backgroundColor: 'var(--color-primary-hover)' },
          }}
        >
          {loading ? (
            <CircularProgress
              size='2rem'
              thickness={5}
              sx={{ color: 'var(--color-primary)' }}
            />
          ) : (
            'Salveaza'
          )}
        </Button>
      </div>
    </form>
  )
}

export default GeneralPage
